import { useMemo } from 'react';

/** Accepts 'primary admin' or 'ca_admin' (case-insensitive). */
const isPrimaryRole = (role: any) =>
  /primary admin|ca_admin/i.test(role.label || role.name || role.text);

/**
 * True if any of the given roles is a Primary Admin, e.g. the current user's
 * roles.
 */
export function hasPrimaryAdminRole(roles: any[] = []): boolean {
  if (!roles || roles.length === 0) return false;
  return roles.some(isPrimaryRole);
}

/**
 * True if `roleId` is a Primary Admin role in `roles`. An empty role
 * ("No role") is never a Primary Admin: falling back to `hasPrimaryAdminRole`
 * here silently saved no-role members with a root node association.
 */
export function isPrimaryAdminRole(
  roles: any[] = [],
  roleId?: string | number | null
): boolean {
  if (!roles || roles.length === 0) return false;
  if (roleId === undefined || roleId === null || roleId === '') return false;

  return roles.some(
    (role: any) =>
      (String(role.id) === String(roleId) ||
        String(role.value) === String(roleId)) &&
      isPrimaryRole(role)
  );
}

/** Hook version of `isPrimaryAdminRole`, for a selected role in a form. */
export function useIsPrimaryAdminRole(
  roles: any[] = [],
  roleId?: string | number | null
): boolean {
  return useMemo(() => isPrimaryAdminRole(roles, roleId), [roles, roleId]);
}

/** Hook version of `hasPrimaryAdminRole`, for the current user's roles. */
export function useIsPrimaryAdmin(roles: any[] = []): boolean {
  return useMemo(() => hasPrimaryAdminRole(roles), [roles]);
}

export default useIsPrimaryAdmin;
